import { FormFieldInput } from '../../../../ui/gzForm/formField/component/FormFieldInput';
import { css, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { GzClearableInputWebComponent } from '../../../../ui/gzInput';
import { ButtonSize, FontSize } from '../../../../ui/gzIconButton/types';
import { CustomRuleValidator } from '../common/validators';
import { GzInputErrorWebComponent } from '../../../../ui/gzForm/inputError';
import { GzIconButtonWebComponent } from '../../../../ui/gzIconButton';

@customElement('gz-integrity-monitor-input-with-chips')
export class IntegrityMonitorInputWithChipsWebComponent extends FormFieldInput<string[]> {
  @property({ type: Array, attribute: true, reflect: true })
  value: string[] = [];

  @property({ type: String })
  placeholder = '';

  @property({ type: Number })
  maxValues = 32;

  @query('gz-input')
  public inputComponent: GzClearableInputWebComponent;

  @query('gz-input-error')
  public inputError: GzInputErrorWebComponent;

  @query('gz-icon-button')
  public buttonComponent: GzIconButtonWebComponent;

  public validator: CustomRuleValidator<string> | null = null;

  public translations: any;

  public getFormValue(): string[] {
    return this.value;
  }

  public setFormValue(value: string[]): void {
    this.value = value;
    this.clearInputComponent();
    this.emitChangeEvent();
  }

  public markAsInvalid(): void {
    super.markAsInvalid();

    if (this.inputComponent) this.inputComponent.markAsInvalid();
  }

  public markAsValid(): void {
    super.markAsValid();

    if (this.inputComponent) this.inputComponent.markAsValid();
    if (this.inputError) this.inputError.Error = null;
  }

  public removeInvalid(): void {
    if (!this.validator) return;

    this.value = this.value.filter(path => this.validator && this.validator(path).valid);

    this.emitChangeEvent();
  }

  static get styles() {
    return css`
      .wrapper {
        display: flex;
        align-items: center;
      }

      #chip-container {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        cursor: default;
        width: 700px;
        margin-top: 8px;

        &.disabled {
          pointer-events: none;
        }
      }

      .mr-2 {
        margin-right: 1em;
      }
    `;
  }

  private renderItems(text: string): TemplateResult {
    return html` <gz-chip
      text=${text}
      notSelectable
      removable
      ?disabled="${this.disabled}"
      @removeChip="${() => this.handleRemoveItem(text)}"
    ></gz-chip>`;
  }

  render() {
    return html`
      <div class="wrapper">
        <gz-input
          class="mr-2"
          placeholder="${this.placeholder}"
          inputwidth="240"
          @change="${this.handleChange}"
          ?disabled="${this.disabled}"
        ></gz-input>
        <gz-icon-button
          buttontype="square"
          buttonfill="primary"
          icon="ds-icon-add"
          size="${ButtonSize.SIZE_32}"
          fontSize="${FontSize.ICON_SIZE_20}"
          disabled
          @click="${this.handleClick}"
        ></gz-icon-button>
      </div>
      <gz-input-error></gz-input-error>
      <div id="chip-container" class="${this.disabled ? 'disabled' : ''}">${this.value.map(chip => this.renderItems(chip))}</div>
    `;
  }

  private handleChange(e: CustomEvent) {
    this.buttonComponent.disabled = e.detail.sourceEvent.length === 0 || this.value.length >= this.maxValues;
  }

  private handleClick(e: Event) {
    const input = this.inputComponent.value;

    if (input.length === 0) return;

    if (this.value.find(x => x === input)) {
      this.inputError.Error = this.translations.errorDuplicateValueIntroduced;
      this.inputComponent.markAsInvalid();
      return;
    }

    if (this.validator) {
      const res = this.validator(input);
      if (!res.valid) {
        this.inputError.Error = res.error;
        this.inputComponent.markAsInvalid();
        return;
      }
    }

    const newItems = this.value.slice();
    newItems.push(input);
    this.value = newItems;

    this.clearInputComponent();

    this.inputError.Error = null;
    this.inputComponent.markAsValid();

    this.emitChangeEvent();
  }

  private handleRemoveItem(item: string) {
    const newItems = this.value.slice();
    newItems.splice(newItems.indexOf(item), 1);
    this.value = newItems;

    this.buttonComponent.disabled = this.inputComponent.value.length === 0 || this.value.length >= this.maxValues;

    this.emitChangeEvent();
  }

  private emitChangeEvent() {
    const event = new CustomEvent('change', {
      detail: {
        value: this.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  private clearInputComponent() {
    this.inputComponent.clearInputValue(new Event('clear'));
  }
}
